<template>
  <main class="page-operative page-operative-balance-cash-flow d-flex flex-column h-100">
    <div class="flex-grow-1 container-fluid d-flex flex-column h-100">
      <div class="d-flex align-items-center justify-content-between flex-wrap flex-md-nowrap mb-5">
        <PageTitle class="mb-2 mb-md-0">Cash flow</PageTitle>

        <div class="actions">
          <button class="btn btn-primary d-inline mr-2" @click.prevent="toggleShow">
            <template v-if="show === 'month'">Visualizza per annualità</template>
            <template v-else>Visualizza per mensilità</template>
          </button>
          <b-dropdown id="download-dropdown" class="d-inline" :no-caret="true" variant="outline-primary">
            <template v-slot:button-content>
              <i class="fas fa-ellipsis-v mr-2"></i>
              Stampa
            </template>
            <b-dropdown-item @click="printCashFlow">Stampa cash flow</b-dropdown-item>
            <b-dropdown-item @click="printBusinessPlan">Stampa business plan</b-dropdown-item>
          </b-dropdown>
        </div>
      </div>

      <div class="flex-grow-1 d-flex flex-column h-100 overflow-auto">
        <LoadingDataLoader v-if="isLoadingData"></LoadingDataLoader>
        <ErrorOnLoadingDataFeedback v-else-if="errorLoadingData"></ErrorOnLoadingDataFeedback>
        <template v-else-if="show === 'month'">
          <div class="overflow-auto report-texts-size">
            <OperativeTable dense sticky>
              <tbody>
              <OperativeTableBodyHeader :years="monthsTableHeadings" show-years></OperativeTableBodyHeader>
              <OperativeTableBodyRow :values="monthsTableRows.ricavi">
                Ricavi
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow v-if="isModuleSubsidizedFinanceEnabled" :values="monthsTableRows.contributiFondoPerduto">
                Contributi a fondo perduto
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.utilizzoCreditoImposta">
                Utilizzo credito d'imposta
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.costi">
                Costi
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.personale">
                Personale
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.investimenti">
                Investimenti
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.tasse">
                Tasse
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.flussi" strong>
                Flussi
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>

              <OperativeTableBodyRow :values="monthsTableRows.deltaClienti">
                ∆ Clienti
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.deltaCreditiTributari">
                ∆ Crediti Tributari
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.deltaCreditoIva">
                ∆ Credito IVA
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow v-if="isModuleSubsidizedFinanceEnabled" :values="monthsTableRows.deltaCreditiContributiDaErogare">
                ∆ Crediti per contributi
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.deltaCreditoImposta">
                ∆ Credito d'imposta
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.deltaAltriCrediti">
                ∆ Altri crediti
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.deltaRiscontiPassivi">
                ∆ Risconti
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.deltaFornitori">
                ∆ Fornitori
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.deltaDebitoIva">
                ∆ Debito IVA
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.deltaDebitiTributariImposte">
                ∆ Debiti Tributari per Imposte
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.deltaDebitiTributariRitenute">
                ∆ Debiti Tributari per Ritenute
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.deltaDebitiEntiPrevidenziali">
                ∆ Debiti verso Enti Previdenziali
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.deltaAltriDebiti">
                ∆ Altri Debiti
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.flussoWorkingCapital" strong>
                Flusso di Working Capital
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.flussoCassaOperativo" strong>
                Flusso di Cassa Operativo
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>

              <OperativeTableBodyRow :values="monthsTableRows.conferimentiEquity">
                Conferimenti Equity
                <template #cell-content="{ value }">{{ value | money }}</template>
                </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.dividendi">
                Dividendi
                <template #cell-content="{ value }">{{ value | money }}</template>
                </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.deltaFondi">
                ∆ Fondi
                <template #cell-content="{ value }">{{ value | money }}</template>
                </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.erogazioniFinanziamentiMLTermine">
                Erogazioni Finanziamenti a M/L Termine
                <template #cell-content="{ value }">{{ value | money }}</template>
                </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.servizioDebito">
                Servizio del debito
                <template #cell-content="{ value }">{{ value | money }}</template>
                </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.breveTermine">
                Oneri Finanziari a Breve Termine
                <template #cell-content="{ value }">{{ value | money }}</template>
                </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.flussoCassaGestione" strong>
                Flusso di Cassa della Gestione
                <template #cell-content="{ value }">{{ value | money }}</template>
                </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.flussoCassaCumulato">
                Flusso di Cassa Cumulato
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.disavanzo">
                Disavanzo
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.DSCR">
                DSCR
                <template #cell-content="{ value }">
                  <template v-if="value === null || value >= 100">&gt;=100</template>
                  <template v-else-if="value < 0">&lt;0</template>
                  <template v-else>{{ value | round }}</template>
                </template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.DSCRCategory">
                Indicatore di crisi
                <template #cell-content="{ value }">
                  <Semaphore :value="value" :label="value | alarm" />
                  {{ value | alarm }}
                </template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.cassa">
                Cassa
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              </tbody>
            </OperativeTable>
          </div>
        </template>
        <template v-else>
          <AnnualCashFlowTable
            class="report-texts-size"
            :isModuleSubsidizedFinanceEnabled="isModuleSubsidizedFinanceEnabled"
            :yearsTableHeadings="yearsTableHeadings"
            :yearsTableRows="yearsTableRows"
          />
        </template>
      </div>
    </div>
  </main>
</template>

<script>

import operativePageMixin from '@/mixins/operativePage.js';
import loadingDataMixin from '@/mixins/loadingData.js';
import computedFinancialDataMixin from '@/mixins/computedFinancialData.js';
import formatMoneyFilter from '@/filters/formatMoney.js';
import formatAlertFilter from '@/filters/formatAlert.js';
import formatRoundFilter from '@/filters/formatRound.js';
import { companyHasModule } from '@/helpers/modules.js';

import { BDropdown, BDropdownItem } from 'bootstrap-vue';
import Semaphore from '@/views/components/Operative/Semaphore';

export default {
  mixins: [operativePageMixin, loadingDataMixin, computedFinancialDataMixin],
  components: {
    PageTitle: () => import('@/views/components/Layout/LayoutOperative/PageTitle.vue'),
    ErrorOnLoadingDataFeedback: () => import('@/views/components/Operative/ErrorOnLoadingDataFeedback.vue'),
    LoadingDataLoader: () => import('@/views/components/Operative/LoadingDataLoader.vue'),
    OperativeTable: () => import('@/views/components/Operative/OperativeTable.vue'),
    OperativeTableBodyHeader: () => import('@/views/components/Operative/OperativeTableBodyHeader.vue'),
    OperativeTableBodyRow: () => import('@/views/components/Operative/OperativeTableBodyRow.vue'),
    AnnualCashFlowTable: () => import('@/views/components/Operative/OperativeTable/AnnualCashFlowTable.vue'),
    Semaphore,
    BDropdown,
    BDropdownItem,
  },
  filters: {
    money: formatMoneyFilter,
    alarm: formatAlertFilter,
    round: formatRoundFilter,
  },
  data () {
    return {
      show: 'year',
      years: [],
      months: [],
    };
  },
  computed: {
    isModuleSubsidizedFinanceEnabled () {
      return companyHasModule(this.company, 'SUBSIDIZED_FINANCE');
    },
    isModuleProjectsEnabled () {
      return companyHasModule(this.company, 'PROJECTS');
    },
    monthsTableHeadings () {
      return this.convertToMonthsHeading(this.months);
    },
    monthsTableRows () {
      return this.convertToRowGroupAutomatically(this.months);
    },
    yearsTableHeadings () {
      return this.convertToYearsHeading(this.years);
    },
    yearsTableRows () {
      return this.convertToRowGroupAutomatically(this.years);
    },
  },
  methods: {
    retrieveData () {
      if (!this.company) {
        return;
      }

      this.resetErrorOnLoadingData();
      this.setDataLoading();

      if (this.show === 'month') {
        this.$api.getComputedCashFlowMensile(this.company?.id)
          .then(res => {
            this.months = res?.data?.months || [];
          })
          .catch(this.setErrorOnLoadingData)
          .finally(this.setDataLoaded)
        ;

        return;
      }

      this.$api.getComputedCashFlow(this.company?.id)
        .then(res => {
          this.years = res?.data?.years || [];
        })
        .catch(this.setErrorOnLoadingData)
        .finally(this.setDataLoaded)
      ;
    },
    toggleShow () {
      if (this.show === 'month') {
        this.show = 'year';
      } else {
        this.show = 'month';
      }

      this.retrieveData();
    },
    printCashFlow () {
      this.$router.push({
        name: 'operative-company.balance.print',
        params: {
          section: 'cashFlow',
        },
        query: {
          forecast: this.$route.query?.forecast || null,
        },
      });
    },
    printBusinessPlan () {
      this.$router.push({
        name: 'operative-company.balance.print',
        params: {
          section: 'businessPlan',
        },
        query: {
          forecast: this.$route.query?.forecast || null,
        },
      });
    },
  },
  watch: {
    company () {
      this.retrieveData();
    },
  },
};

</script>
